/**
 * API Service for QA AI Agent
 *
 * Handles all API calls to the QA Agent backend
 */

import { ChatRequest, ChatResponse, StreamEvent, HealthStatus, ToolCategory } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';

/**
 * Check agent health status
 */
export const getHealthStatus = async (): Promise<HealthStatus> => {
  // Health endpoint is at /health/live (proxied to backend via Vite)
  const response = await fetch('/health/live', {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('QA Agent is not available');
  }

  return response.json();
};

/**
 * Send a chat message (non-streaming)
 */
export const chat = async (request: ChatRequest): Promise<ChatResponse> => {
  // Only include project_id in the request body if it's truthy
  const body: Record<string, any> = { message: request.message };
  if (request.conversation_id) body.conversation_id = request.conversation_id;
  if (request.project_id) body.project_id = request.project_id;
  if (request.context) body.context = request.context;

  const response = await fetch(`${API_URL}/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to send message');
  }

  return response.json();
};

/**
 * Send a chat message with streaming response
 */
export const streamChat = async (
  request: ChatRequest,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  // Only include project_id in the request body if it's truthy
  const body: Record<string, any> = { message: request.message };
  if (request.conversation_id) body.conversation_id = request.conversation_id;
  if (request.project_id) body.project_id = request.project_id;
  if (request.context) body.context = request.context;

  // Retry transient network errors (connection refused, DNS failures, pod rollover)
  const MAX_RETRIES = 2;
  const RETRY_DELAY_MS = 2000;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    let streamStarted = false;
    try {
      const response = await fetch(`${API_URL}/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        const detail = error.detail;
        const message = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ') : 'Failed to stream chat';
        // Don't retry HTTP errors (auth failures, validation errors, etc.) — only network errors
        throw new Error(message);
      }

      // Connection succeeded — mark stream as started so we don't retry
      // (retrying after stream reading has begun would duplicate the request)
      streamStarted = true;
      return await _readSSEStream(response, onEvent);
    } catch (err: any) {
      if (err.name === 'AbortError') throw err;

      // Never retry after the stream has started being read — the backend
      // already received and began processing the request. Retrying would
      // send a duplicate message and potentially re-execute side-effects.
      if (streamStarted) throw err;

      const isNetworkError = err instanceof TypeError || /network|fetch|ERR_CONNECTION|ECONNREFUSED/i.test(err.message);
      if (isNetworkError && attempt < MAX_RETRIES) {
        lastError = err;
        console.warn(`Chat stream connection failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}), retrying in ${RETRY_DELAY_MS}ms...`, err.message);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        continue;
      }
      throw err;
    }
  }

  throw lastError || new Error('Failed to connect after retries');
};

/** Read and parse an SSE response stream. Extracted to support retry logic. */
const _readSSEStream = async (
  response: Response,
  onEvent: (event: StreamEvent) => void,
): Promise<void> => {

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let receivedDone = false;

  while (true) {
    let readResult: ReadableStreamReadResult<Uint8Array>;
    try {
      readResult = await reader.read();
    } catch (readError: any) {
      // Stream read failed — connection dropped (nginx timeout, backend crash, etc.)
      throw new Error(
        readError?.message || 'Connection to server was lost. Please try again.'
      );
    }

    const { done, value } = readResult;
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (data === '[DONE]') {
          receivedDone = true;
          onEvent({ type: 'complete', success: true });
          return;
        }
        try {
          const event = JSON.parse(data) as StreamEvent;
          onEvent(event);
        } catch (e) {
          console.error('Failed to parse SSE data:', e);
        }
      }
    }
  }

  // Stream ended without [DONE] — connection was dropped prematurely
  if (!receivedDone) {
    throw new Error(
      'Connection to server was lost before the response completed. Please try again.'
    );
  }
};

/**
 * Send thumbs up/down feedback for a saved assistant message
 */
export const submitFeedback = async (
  messageId: string,
  rating: 'up' | 'down',
  comment?: string
): Promise<void> => {
  const response = await fetch(`${API_URL}/feedback`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message_id: messageId, rating, comment: comment || undefined }),
  });
  if (!response.ok) {
    throw new Error(`Feedback failed (${response.status})`);
  }
};

/**
 * Handle approval action (approve or reject a pending action)
 */
export const handleApproval = async (
  approvalId: string,
  action: 'approve' | 'reject',
  comment?: string
): Promise<any> => {
  const response = await fetch(`${API_URL}/approvals/${approvalId}/decide`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-approval-channel': 'button',
    },
    body: JSON.stringify({
      approved: action === 'approve',
      reason: comment || undefined,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    const err: Error & { status?: number } = new Error(
      error.detail || 'Failed to handle approval'
    );
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * List pending approval requests
 */
export const listPendingApprovals = async (): Promise<any[]> => {
  const response = await fetch(`${API_URL}/approvals`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list approvals');
  }

  return response.json();
};

/**
 * Submit a HITL (Human-In-The-Loop) response to unblock the agent.
 * Called when the user responds to a request_user_input gate.
 */
export const submitHitlResponse = async (
  sessionId: string,
  requestId: string,
  response: Record<string, any>
): Promise<any> => {
  const res = await fetch(`${API_URL}/chat/sessions/${sessionId}/hitl-response`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      request_id: requestId,
      response,
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || 'Failed to submit HITL response');
  }

  return res.json();
};

/**
 * Pending HITL requests of a chat session — used for gates whose prompt the
 * server composes (security_create_issue's finding summary).
 */
export const getPendingHitlRequests = async (
  sessionId: string
): Promise<{ request_id: string; event_type: string; message: string; data: Record<string, any> }[]> => {
  const res = await fetch(`${API_URL}/chat/sessions/${sessionId}/hitl-pending`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || 'Failed to load pending HITL requests');
  }

  const body = await res.json();
  return Array.isArray(body?.requests) ? body.requests : [];
};

/**
 * Get conversation history
 */
export const getConversationHistory = async (conversationId: string): Promise<any> => {
  const response = await fetch(`${API_URL}/chat/conversations/${conversationId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get conversation history');
  }

  return response.json();
};

// ============================================================================
// Tool Categories - Based on QA AI Agent Design
// ============================================================================

export const TOOL_CATEGORIES: ToolCategory[] = [
  {
    name: 'Bug Management',
    description: 'Tools for creating, triaging, and managing bugs',
    icon: 'bug',
    tools: [
      {
        name: 'create_jira_bug',
        description: 'Create a new JIRA bug ticket with AI-generated summary',
        example_prompt: 'Create a bug ticket for the login failure on the checkout page',
      },
      {
        name: 'triage_failure',
        description: 'Analyze and triage test failures automatically',
        example_prompt: 'Triage the failures from test run TR-123',
      },
      {
        name: 'duplicate_detector',
        description: 'Detect duplicate bugs across tickets',
        example_prompt: 'Check if this bug is a duplicate: "Login button not working"',
      },
      {
        name: 'root_cause_analyzer',
        description: 'Perform root cause analysis on failures',
        example_prompt: 'Analyze root cause for the payment processing failure',
      },
    ],
  },
  {
    name: 'Coverage Analysis',
    description: 'Tools for analyzing and improving test coverage',
    icon: 'coverage',
    tools: [
      {
        name: 'coverage_analyzer',
        description: 'Analyze code coverage metrics from SonarQube',
        example_prompt: 'Show me the coverage analysis for the auth module',
      },
      {
        name: 'gap_identifier',
        description: 'Identify coverage gaps in test suites',
        example_prompt: 'Find coverage gaps in our API tests',
      },
      {
        name: 'coverage_trend',
        description: 'Track coverage trends over time',
        example_prompt: 'Show coverage trends for the last 30 days',
      },
    ],
  },
  {
    name: 'Test Planning',
    description: 'Tools for generating and optimizing test plans',
    icon: 'plan',
    tools: [
      {
        name: 'generate_test_plan',
        description: 'Generate comprehensive test plans from requirements',
        example_prompt: 'Generate a test plan for the new user registration feature',
      },
      {
        name: 'risk_analyzer',
        description: 'Analyze risk areas in the application',
        example_prompt: 'Identify high-risk areas for the payment module',
      },
      {
        name: 'prioritize_tests',
        description: 'Prioritize tests based on risk and coverage',
        example_prompt: 'Prioritize tests for the upcoming release',
      },
      {
        name: 'estimate_effort',
        description: 'Estimate testing effort for features',
        example_prompt: 'Estimate testing effort for the dashboard redesign',
      },
    ],
  },
  {
    name: 'Reporting',
    description: 'Tools for generating test reports and metrics',
    icon: 'report',
    tools: [
      {
        name: 'generate_report',
        description: 'Generate comprehensive test execution reports',
        example_prompt: 'Generate a test report for sprint 23',
      },
      {
        name: 'slack_summary',
        description: 'Send test summaries to Slack channels',
        example_prompt: "Send today's test summary to #qa-alerts",
      },
      {
        name: 'export_metrics',
        description: 'Export test metrics to various formats',
        example_prompt: "Export this month's metrics to CSV",
      },
    ],
  },
  {
    name: 'Test Data',
    description: 'Tools for generating and managing test data',
    icon: 'data',
    tools: [
      {
        name: 'generate_test_data',
        description: 'Generate realistic test data based on schemas',
        example_prompt: 'Generate 100 test user records with valid emails',
      },
      {
        name: 'data_masker',
        description: 'Mask sensitive data for testing',
        example_prompt: 'Mask PII data in the test database export',
      },
      {
        name: 'seed_database',
        description: 'Seed test databases with generated data',
        example_prompt: 'Seed the staging database with test data',
      },
    ],
  },
  {
    name: 'Environment',
    description: 'Tools for managing test environments',
    icon: 'environment',
    tools: [
      {
        name: 'provision_environment',
        description: 'Provision test environments on demand',
        example_prompt: 'Provision a new staging environment for testing',
      },
      {
        name: 'health_check',
        description: 'Check health of test environments',
        example_prompt: 'Run health checks on the QA environment',
      },
      {
        name: 'teardown_environment',
        description: 'Safely teardown test environments',
        example_prompt: 'Teardown the temp-testing environment',
      },
    ],
  },
  {
    name: 'API Testing',
    description: 'Tools for API test generation and validation',
    icon: 'api',
    tools: [
      {
        name: 'generate_api_tests',
        description: 'Generate API tests from OpenAPI specifications',
        example_prompt: 'Generate API tests from the swagger.json file',
      },
      {
        name: 'contract_validator',
        description: 'Validate API contracts and schemas',
        example_prompt: 'Validate the response schema for /api/users endpoint',
      },
      {
        name: 'mock_generator',
        description: 'Generate mock API responses',
        example_prompt: 'Generate mocks for the payment gateway API',
      },
    ],
  },
  {
    name: 'AI Testing',
    description: 'AI-powered test authoring, self-healing locators, visual assertions, and app exploration',
    icon: 'ai_testing',
    tools: [
      {
        name: 'nl_test_author',
        description: 'Convert natural language descriptions into executable test steps',
        example_prompt: 'Test that the login page works at https://example.com',
      },
      {
        name: 'nl_visual_assert',
        description: 'Use AI vision to validate UI state with natural language assertions',
        example_prompt: 'Visually check that the dashboard shows the correct charts',
      },
      {
        name: 'nl_app_explore',
        description: 'Autonomously explore an application and discover user flows',
        example_prompt: 'Explore the app at https://example.com and discover user flows',
      },
      {
        name: 'nl_self_heal',
        description: 'Automatically repair broken element selectors in UI tests',
        example_prompt: 'Heal the broken selectors in the checkout test',
      },
    ],
  },
  {
    name: 'Automation Maintenance',
    description: 'Tools for maintaining automated tests',
    icon: 'automation',
    tools: [
      {
        name: 'fix_locator',
        description: 'Fix broken element locators in UI tests',
        example_prompt: 'Fix the broken locator for the login button',
      },
      {
        name: 'convert_manual_test',
        description: 'Convert manual test cases to automated scripts',
        example_prompt: 'Convert TC-456 from manual to Selenium script',
      },
      {
        name: 'heal_test',
        description: 'Self-heal flaky tests automatically',
        example_prompt: 'Heal the flaky checkout test',
      },
    ],
  },
];

export const QUICK_ACTIONS = [
  {
    category: 'Analysis',
    prompts: [
      'Analyze failures from the last test run',
      'Show me flaky tests in the project',
      'What are the most common failure patterns?',
      'Identify high-risk areas in the codebase',
    ],
  },
  {
    category: 'Reporting',
    prompts: [
      'Generate a test summary for today',
      'Show coverage trends for this sprint',
      'Create a regression report',
      'Export test metrics to CSV',
    ],
  },
  {
    category: 'Bug Management',
    prompts: [
      'Create a bug ticket for the latest failure',
      'Triage open bugs by severity',
      'Find duplicate bug tickets',
      'Analyze root cause for recent failures',
    ],
  },
  {
    category: 'Test Generation',
    prompts: [
      'Generate test cases for the login feature',
      'Create API tests from the OpenAPI spec',
      'Convert manual test to automation',
      'Generate test data for user registration',
    ],
  },
  {
    category: 'NL Testing',
    prompts: [
      'Test that the login page works at https://example.com',
      'Explore the app and discover user flows',
      'Visually check the dashboard looks correct',
      'Run a test for the checkout flow',
    ],
  },
  {
    category: 'Site Analysis',
    prompts: [
      'I want to test https://example.com',
      'Full site analysis for https://example.com',
      'Analyze this website: https://example.com',
      'Test this site: https://example.com',
    ],
  },
];

/**
 * Download generated code from a site analysis as a ZIP file
 */
export const downloadGeneratedCode = async (analysisId: string): Promise<void> => {
  const url = `${API_URL}/site-analysis/${analysisId}/code/download`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download generated code');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = `site_analysis_${analysisId.slice(0, 8)}_tests.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * List files in a project workspace
 */
export const listWorkspaceFiles = async (projectId: string): Promise<{ files: string[] }> => {
  const response = await fetch(`${API_URL}/projects/${projectId}/workspace/files`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list workspace files');
  }

  return response.json();
};

/**
 * Get content of a specific file in the project workspace
 */
export const getWorkspaceFileContent = async (
  projectId: string,
  filePath: string
): Promise<{ content: string; path: string }> => {
  const encodedPath = encodeURIComponent(filePath);
  const response = await fetch(
    `${API_URL}/projects/${projectId}/workspace/files/${encodedPath}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get file content');
  }

  return response.json();
};

const apiService = {
  getHealthStatus,
  chat,
  streamChat,
  handleApproval,
  listPendingApprovals,
  getConversationHistory,
  downloadGeneratedCode,
  listWorkspaceFiles,
  getWorkspaceFileContent,
  TOOL_CATEGORIES,
  QUICK_ACTIONS,
};

export default apiService;
